/**
 * Self-regularization — the rules behind the screens, with no API or DOM in them.
 *
 * "Self" because the vendor is not involved: what is already on the record (GSTIN, PAN, bank
 * account, Udyam number) is verified straight against the registries, for a per-call price, and
 * an admin approves what came back into the record. Everything here is what the admin sees
 * before paying (the estimate) and how a run is summarised afterwards; the mock and, later, the
 * real service both lean on it.
 */

/** The verifications on offer. Prices are placeholders until the provider's rate card is wired in. */
export const CHECKS = [
  {
    key: 'gst',
    label: 'GST registration',
    icon: 'receipt_long',
    description: 'Confirms the GSTIN is live and pulls the trade name (the vendor name), legal name, address and turnover slab the GST registry holds.',
    needs: 'gst_number',
    needsLabel: 'GSTIN on record',
    facts: ['Vendor name', 'Legal name', 'Trade name', 'GSTIN status', 'Registration date', 'Constitution', 'Registered state', 'Registered pincode', 'Turnover slab', 'PAN'],
    price_inr: 3,
    available: true,
  },
  {
    key: 'pan',
    label: 'PAN',
    icon: 'badge',
    description:
      'Takes the PAN from the GSTIN — every GSTIN carries the PAN in its middle ten characters — and checks it against the PAN on record. No registry call, nothing charged.',
    needs: 'gst_number',
    needsLabel: 'GSTIN on record',
    facts: ['PAN', 'Category'],
    price_inr: 0,
    available: true,
    // Answered from our own records: no call, never "not enabled".
    local: true,
  },
  {
    key: 'bank',
    label: 'Bank account',
    icon: 'account_balance',
    description: 'Verifies the account exists and whose name it is in, so payments to it are safe.',
    needs: 'bank',
    needsLabel: 'bank account on record',
    facts: ['Account holder', 'Name match', 'Bank', 'Branch', 'Account status'],
    price_inr: 4,
    available: true,
  },
  {
    key: 'udyam',
    label: 'MSME / Udyam',
    icon: 'storefront',
    description: 'Confirms the Udyam registration and its current classification, which decides payment terms.',
    needs: 'msme_number',
    needsLabel: 'Udyam number on record',
    facts: ['Enterprise name', 'Category', 'Major activity', 'Classification year', 'Date of incorporation'],
    price_inr: 2,
    available: true,
  },
];

/**
 * One hue per check, written out in full because Tailwind reads class names literally — a
 * template-built class name would never reach the stylesheet. `bg`/`text` are for a chip, `bar`
 * for a progress segment, `ring` for a selected card, `icon` for the icon tile.
 */
export const CHECK_TONE = {
  gst: {
    bg: 'bg-cos-check-gst-faint',
    text: 'text-cos-check-gst-deep',
    bar: 'bg-cos-check-gst',
    border: 'border-cos-check-gst/35',
    ring: 'border-cos-check-gst shadow-[0_0_0_3px_rgba(15,168,155,0.10)]',
    icon: 'bg-cos-check-gst-faint text-cos-check-gst-deep',
    solid: 'bg-cos-check-gst text-white',
  },
  pan: {
    bg: 'bg-cos-check-pan-faint',
    text: 'text-cos-check-pan-deep',
    bar: 'bg-cos-check-pan',
    border: 'border-cos-check-pan/35',
    ring: 'border-cos-check-pan shadow-[0_0_0_3px_rgba(79,107,168,0.12)]',
    icon: 'bg-cos-check-pan-faint text-cos-check-pan-deep',
    solid: 'bg-cos-check-pan text-white',
  },
  bank: {
    bg: 'bg-cos-check-bank-faint',
    text: 'text-cos-check-bank-deep',
    bar: 'bg-cos-check-bank',
    border: 'border-cos-check-bank/35',
    ring: 'border-cos-check-bank shadow-[0_0_0_3px_rgba(180,132,46,0.12)]',
    icon: 'bg-cos-check-bank-faint text-cos-check-bank-deep',
    solid: 'bg-cos-check-bank text-white',
  },
  udyam: {
    bg: 'bg-cos-check-udyam-faint',
    text: 'text-cos-check-udyam-deep',
    bar: 'bg-cos-check-udyam',
    border: 'border-cos-check-udyam/35',
    ring: 'border-cos-check-udyam shadow-[0_0_0_3px_rgba(154,85,120,0.12)]',
    icon: 'bg-cos-check-udyam-faint text-cos-check-udyam-deep',
    solid: 'bg-cos-check-udyam text-white',
  },
};

const NEUTRAL_TONE = {
  bg: 'bg-[rgba(70,80,72,0.06)]',
  text: 'text-cos-ink-secondary',
  bar: 'bg-cos-ink-muted',
  border: 'border-cos-line',
  ring: 'border-cos-teal shadow-cos-teal-glow',
  icon: 'bg-cos-surface-hover text-cos-ink-secondary',
  solid: 'bg-cos-ink text-white',
};

export const toneFor = (key) => CHECK_TONE[key] || NEUTRAL_TONE;

export function checkByKey(key) {
  return CHECKS.find((c) => c.key === key);
}

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * What one vendor costs across the ticked checks — the rail's number before any vendor is ticked,
 * so "₹11 per vendor" is on screen from the first step. `catalogue` is the CHECKS list with the
 * server's rate card merged over it; `checkKeys` may be an array or the wizard's Set.
 */
export function perVendorTotal(checkKeys, catalogue = CHECKS) {
  const wanted = new Set(checkKeys);
  return round2(catalogue.filter((c) => wanted.has(c.key)).reduce((n, c) => n + Number(c.price_inr || 0), 0));
}

/**
 * What a run would cost before anything is called: for each check, the vendors whose record
 * carries the input it needs are runnable and charged; the rest are skipped, explained, free.
 * `inputsOf(vendor)` returns the `needs` keys the record can supply.
 */
export function estimateRun(checkKeys, vendors, inputsOf) {
  const per_check = [];
  for (const key of checkKeys) {
    const check = checkByKey(key);
    if (!check) continue;
    let runnable = 0;
    for (const v of vendors) if ((inputsOf(v) || []).includes(check.needs)) runnable += 1;
    const skipped = vendors.length - runnable;
    per_check.push({
      key,
      runnable,
      skipped,
      skipped_reason: skipped > 0 ? `No ${check.needsLabel} — skipped, not charged` : '',
      // A local check reads the record: nobody is called and nothing is charged.
      calls: check.local ? 0 : runnable,
      cost_inr: check.local ? 0 : round2(runnable * check.price_inr),
    });
  }
  return {
    vendors: vendors.length,
    per_check,
    calls: per_check.reduce((n, c) => n + c.calls, 0),
    cost_inr: round2(per_check.reduce((n, c) => n + c.cost_inr, 0)),
  };
}

const norm = (v) => String(v ?? '').trim().replace(/\s+/g, ' ').toUpperCase();

/**
 * One fact, record against registry. The registry returning nothing never overwrites what we
 * hold, so that reads as a match; an empty record is filled ("new"); a difference is an update.
 * A difference a person should look at first is marked "check" by the caller, not here.
 */
export function factOutcome(current, verified) {
  const v = norm(verified);
  if (!v) return 'match';
  const c = norm(current);
  if (!c) return 'new';
  return c === v ? 'match' : 'update';
}

/** A check's status from its facts: attention beats changed beats match. */
export function checkStatusFromFacts(facts) {
  let changed = false;
  for (const f of facts) {
    if (f.outcome === 'check') return 'attention';
    if (f.outcome === 'new' || f.outcome === 'update') changed = true;
  }
  return changed ? 'changed' : 'match';
}

const SETTLED = new Set(['done', 'skipped', 'failed']);
const ATTENTION_CHECKS = new Set(['attention', 'not_found']);

export function vendorNeedsAttention(vendor) {
  return vendor.status === 'done' && vendor.checks.some((c) => ATTENTION_CHECKS.has(c.status));
}

/** The header counters, read at vendor level. `done` is progress: everything no longer queued or verifying. */
export function runCounts(run) {
  const counts = { total: run.vendors.length, done: 0, verified: 0, attention: 0, not_found: 0, skipped: 0, failed: 0, approved: 0, excluded: 0, ack_requested: 0, acknowledged: 0 };
  for (const v of run.vendors) {
    if (SETTLED.has(v.status)) counts.done += 1;
    if (v.status === 'done') counts.verified += 1;
    if (v.status === 'skipped') counts.skipped += 1;
    if (v.status === 'failed') counts.failed += 1;
    if (vendorNeedsAttention(v)) counts.attention += 1;
    if (v.status === 'done' && v.checks.some((c) => c.status === 'not_found')) counts.not_found += 1;
    if (v.approval === 'approved') counts.approved += 1;
    if (v.approval === 'excluded') counts.excluded += 1;
    if (v.acknowledgement === 'requested') counts.ack_requested += 1;
    if (v.acknowledgement === 'acknowledged') counts.acknowledged += 1;
  }
  return counts;
}

/** Rupees actually spent so far: every call that reached the registry, whatever it answered. */
export function costSoFar(run) {
  let n = 0;
  for (const v of run.vendors) for (const c of v.checks) n += Number(c.cost_inr || 0);
  return round2(n);
}

/** Only a vendor a registry answered for has anything to approve. */
export const isApprovable = (vendor) => vendor.status === 'done';

export function deriveRunStatus(run) {
  const vendors = run.vendors || [];
  if (vendors.length === 0) return 'queued';
  const unsettled = vendors.filter((v) => !SETTLED.has(v.status));
  if (unsettled.length > 0) {
    return !run.started_at && unsettled.length === vendors.length && vendors.every((v) => v.status === 'queued') ? 'queued' : 'running';
  }
  if (vendors.every((v) => v.status === 'failed')) return 'failed';
  const approvable = vendors.filter(isApprovable);
  // Nothing a registry answered for (everyone skipped): the admin still closes it by approving.
  if (approvable.length === 0) return run.approved_at ? 'approved' : 'awaiting_approval';
  const decided = approvable.filter((v) => v.approval && v.approval !== 'pending');
  if (decided.length < approvable.length) return 'awaiting_approval';
  return decided.some((v) => v.approval === 'excluded') ? 'partially_approved' : 'approved';
}

/** Run status → <StatusPill> vocabulary + label. */
export const RUN_STATUS_PILL = {
  queued: { pill: 'pending', label: 'Queued' },
  running: { pill: 'running', label: 'Verifying' },
  awaiting_approval: { pill: 'pending', label: 'Awaiting approval' },
  approved: { pill: 'approved', label: 'Approved' },
  partially_approved: { pill: 'completed', label: 'Partially approved' },
  failed: { pill: 'failed', label: 'Failed' },
};

export const CHECK_STATUS_LABEL = {
  queued: 'Queued',
  running: 'Verifying…',
  match: 'Matches',
  changed: 'Changes found',
  attention: 'Needs attention',
  not_found: 'Not found',
  skipped: 'Skipped',
  failed: 'Failed',
};

export const FACT_OUTCOME_LABEL = { match: 'Match', new: 'New', update: 'Will update', check: 'Check' };

/** After approval the vendor is asked to confirm what was written — the one mail this flow sends. */
export const ACK_LABEL = { none: 'Applied', requested: 'Awaiting acknowledgement', acknowledged: 'Acknowledged' };

// ---- Finding the vendors a run should reach ---------------------------------------------------

/**
 * The needs filter on the vendors step. "Not yet verified" means the record carries the input a
 * check needs and no approved run has confirmed it; "No … on record" means the input itself is
 * missing, which a run cannot fix — those are the vendor's to supply, through the vendor.
 */
export const NEEDS_FILTERS = [
  { key: 'all', label: 'All vendors' },
  { key: 'unverified:any', label: 'Nothing verified yet' },
  ...CHECKS.map((c) => ({ key: `unverified:${c.key}`, label: `${c.label} not yet verified` })),
  // One filter per input: GST and PAN both read the GSTIN, and the vendor is asked for it once.
  ...[...new Map(CHECKS.map((c) => [c.needs, { key: `missing:${c.needs}`, label: `No ${c.needsLabel}` }])).values()],
];

export function matchesNeed(filterKey, { inputs = [], verified = new Set() } = {}) {
  const [kind, arg] = String(filterKey || 'all').split(':');
  if (kind === 'unverified') {
    const unverified = (c) => inputs.includes(c.needs) && !verified.has(c.key);
    if (arg === 'any') return CHECKS.some(unverified);
    const c = checkByKey(arg);
    return !!c && unverified(c);
  }
  if (kind === 'missing') return !inputs.includes(arg);
  return true;
}

const ANSWERED = new Set(['match', 'changed']);

/** Per vendor, the checks an approved run has confirmed — a registry answered, and the answer was applied. */
export function verifiedChecksByVendor(runs) {
  const out = new Map();
  for (const run of runs || []) {
    for (const v of run.vendors || []) {
      if (v.status !== 'done' || v.approval !== 'approved') continue;
      for (const c of v.checks || []) {
        if (!ANSWERED.has(c.status)) continue;
        if (!out.has(v.vendor_id)) out.set(v.vendor_id, new Set());
        out.get(v.vendor_id).add(c.key);
      }
    }
  }
  return out;
}

const count = (n) => Number(n || 0).toLocaleString('en-IN');

/**
 * One line for a vendor-code sheet that has just been read: "3 matched · 2 not found (codes: …)".
 * The unknown codes are named, because "2 not found" alone sends the operator back to the sheet
 * to guess which; `maxCodes` keeps a toast short — the line under the button can show them all.
 */
export function describeSheet(res, { maxCodes = Infinity } = {}) {
  const matched = res.matched || [];
  const missing = res.missing || [];
  const parts = [`${count(matched.length)} matched`];
  if (missing.length) {
    const shown = missing.slice(0, maxCodes);
    const more = missing.length - shown.length;
    parts.push(`${count(missing.length)} not found (codes: ${shown.join(', ')}${more > 0 ? ` +${count(more)} more` : ''})`);
  }
  if (res.duplicates > 0) parts.push(`${count(res.duplicates)} duplicate code${res.duplicates === 1 ? '' : 's'} skipped`);
  if (res.header_matched === false) parts.push('no vendor-code header, so the first column was read');
  return parts.join(' · ');
}

export function formatInr(n) {
  const value = Number(n || 0);
  const digits = Number.isInteger(value) ? 0 : 2;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
}

// ---- The runs list, filtered by check ------------------------------------------------------------

/** The pills above the runs list: "All", then one per check, in the catalogue's order. */
export const RUN_CHECK_FILTERS = [{ key: 'all', label: 'All', icon: 'apps' }, ...CHECKS.map((c) => ({ key: c.key, label: c.label, icon: c.icon }))];

/** The runs that made a check. "All", or a key that is not a check, keeps every run; order is untouched. */
export function filterRunsByCheck(runs, key) {
  const list = Array.isArray(runs) ? runs : [];
  if (!key || key === 'all' || !checkByKey(key)) return list;
  return list.filter((r) => Array.isArray(r.checks) && r.checks.includes(key));
}

/** How many runs each pill holds, so a pill can say so. */
export function runCountsByCheck(runs) {
  const list = Array.isArray(runs) ? runs : [];
  const counts = { all: list.length };
  for (const c of CHECKS) counts[c.key] = list.filter((r) => Array.isArray(r.checks) && r.checks.includes(c.key)).length;
  return counts;
}
